import { useCallback, useEffect, useRef, useState } from 'react';

import useScroll from './useScroll';

interface UseFocusIndexOptions {
  initialIndex?: number;
  maxIndex: number;
  active?: boolean;
  onSelect: (index: number) => void;
  onCancel?: (index: number) => void;
}

const useFocusIndex = ({
  initialIndex = 0, maxIndex, active = true, onSelect, onCancel,
}: UseFocusIndexOptions) => {
  const { scrollTo } = useScroll();
  const [focusIndex, setFocusIndex] = useState(initialIndex);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const scrollToIndex = useCallback((index: number) => {
    const container = containerRef.current;

    if (container) {
      const { id } = container;

      if (id) {
        const item = document.getElementById(`${id}_${index}`);

        if (item) {
          const offsetTop = item.offsetTop - container.offsetTop;

          // Scroll down
          if (offsetTop + item.offsetHeight > container.scrollTop + container.offsetHeight) {
            scrollTo(`${id}_${index}`, {
              scrollContainer: id,
              duration: 300,
            });
          }

          // Scroll up
          if (offsetTop < container.scrollTop) {
            scrollTo(`${id}_${index}`, {
              scrollContainer: id,
              duration: 300,
            });
          }
        }
      }
    }
  }, []);

  useEffect(() => {
    setFocusIndex((focusIndex) => Math.min(focusIndex, maxIndex));
  }, [focusIndex, maxIndex]);

  useEffect(() => {
    if (active) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          const newIndex = Math.min(focusIndex === maxIndex ? 0 : focusIndex + 1, maxIndex);
          setFocusIndex(newIndex);
          scrollToIndex(newIndex);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          const newIndex = Math.min(focusIndex === 0 ? maxIndex : focusIndex - 1, maxIndex);
          setFocusIndex(newIndex);
          scrollToIndex(newIndex);
        } else if (event.key === 'Enter') {
          event.preventDefault();
          onSelect?.(focusIndex);
        } else if (event.key === ' ') {
          const target = event.target as Element;

          if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
            // If the user is typing in an input we don't want the space bar to have special behaviour
            event.preventDefault();
            onSelect?.(focusIndex);
          }
        } else if (event.key === 'Escape') {
          event.preventDefault();
          onCancel?.(focusIndex);
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }

    return undefined;
  }, [maxIndex, focusIndex, active, onCancel, onSelect, scrollToIndex]);

  return [
    focusIndex, setFocusIndex, containerRef, scrollToIndex,
  ] as const;
};

export default useFocusIndex;
